import { useMemo } from 'react';

import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useMemoPromptsQuery } from '@/modules/memos/graphql/MemoPrompts.generated';
import { useCreatableMemoPromptTypes } from '@/modules/memos/hooks/useCreatableMemoPromptTypes';
import {
  CreatableMemoPromptType,
  MEMO_PROMPT_CONFIG,
} from '@/modules/memos/memos.constants';
import { useMemosTabQuery } from '@/modules/memos/MemosTab/graphql/MemosTab.generated';
import { COLORS } from '@/styles/tokens/colors';
import { getNodes } from '@/utils/graphqlUtils';

import { ArtifactTemplateCardProps } from '../components/ArtifactComposer/ArtifactTemplateCard';
import { useArtifactStudioContext } from '../contexts/artifactStudio.context';
import { TEMPLATE_DETAILS } from '../DiagramBuilder.constants';
import { useDiagramBuilderDefaultPromptsQuery } from '../graphql/DiagramTemplates.generated';

/** A Studio template for a diagram or a memo. */
export interface ArtifactTemplate extends ArtifactTemplateCardProps {
  /** The prompt type of the template. Unique across diagrams and memos. */
  id: string;
}

/**
 * Loads the default diagram and memo prompts and maps each prompt type to a
 * template card. Diagram templates come first.
 *
 * A diagram card selects its template for the composer. A memo card calls
 * `onSelectMemoPromptType`. The hook returns memo templates only when
 * `memos_in_studio` is enabled. Memos summarize documents, so the hook also
 * requires that the household has documents.
 */
export function useArtifactTemplates(
  onSelectMemoPromptType: (memoPromptType: CreatableMemoPromptType) => void
) {
  const {
    householdId,
    selectedTemplate,
    setSelectedTemplate,
    referenceImage,
    isValidatingReferenceImage,
  } = useArtifactStudioContext();
  const isMemosInStudioEnabled = useFeatureFlag('memos_in_studio');
  const diagramPromptsQuery = useDiagramBuilderDefaultPromptsQuery();
  const memoPromptsQuery = useMemoPromptsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isMemosInStudioEnabled,
  });
  const documentsQuery = useMemosTabQuery({
    variables: { householdId },
    fetchPolicy: 'cache-and-network',
    skip: !isMemosInStudioEnabled,
  });
  const creatableMemoPromptTypes = useCreatableMemoPromptTypes();

  // A template and a reference image are different ways to start a diagram.
  const diagramTemplatesDisabled =
    Boolean(referenceImage) || isValidatingReferenceImage;
  const diagramPrompts = diagramPromptsQuery.data?.defaultDiagramPrompts;
  const memoPrompts = memoPromptsQuery.data?.defaultMemoPrompts;
  const hasDocuments = getNodes(documentsQuery.data?.documents).length > 0;

  const templates = useMemo((): ArtifactTemplate[] => {
    const diagramTemplates = (diagramPrompts ?? []).map(
      ({ diagramArtifactPromptType }) => {
        const { title, description, Illustration } =
          TEMPLATE_DETAILS[diagramArtifactPromptType];
        return {
          id: diagramArtifactPromptType,
          title,
          description,
          icon: (
            <Illustration
              width="100%"
              height="100%"
              style={{ display: 'block' }}
            />
          ),
          selected: selectedTemplate === diagramArtifactPromptType,
          disabled: diagramTemplatesDisabled,
          onClick: () => setSelectedTemplate(diagramArtifactPromptType),
        };
      }
    );
    if (!hasDocuments) return diagramTemplates;

    // The server returns every default memo type, in its own order. Show the
    // types that the user can create, in the Memos tab card order.
    const serverMemoTypes = new Set(
      (memoPrompts ?? []).map(({ memoType }) => memoType)
    );
    const memoTemplates = creatableMemoPromptTypes
      .filter((memoPromptType) => serverMemoTypes.has(memoPromptType))
      .map((memoPromptType) => {
        const { title, description, Icon } = MEMO_PROMPT_CONFIG[memoPromptType];
        return {
          id: memoPromptType,
          title,
          description,
          // The diagram illustrations draw their shapes in GRAY[600].
          icon: <Icon size={32} color={COLORS.GRAY[600]} />,
          onClick: () => onSelectMemoPromptType(memoPromptType),
        };
      });
    return [...diagramTemplates, ...memoTemplates];
  }, [
    diagramPrompts,
    memoPrompts,
    hasDocuments,
    creatableMemoPromptTypes,
    selectedTemplate,
    setSelectedTemplate,
    diagramTemplatesDisabled,
    onSelectMemoPromptType,
  ]);

  return {
    templates,
    // Wait for the documents to load, so the notice does not flash.
    showMemoDocumentsNotice: Boolean(documentsQuery.data) && !hasDocuments,
    loading: diagramPromptsQuery.loading || memoPromptsQuery.loading,
    error: diagramPromptsQuery.error ?? memoPromptsQuery.error,
  };
}
